import {
	CheckCircleFilled,
	DeleteOutlined,
	EditOutlined,
	InfoCircleOutlined,
	PlusOutlined,
	SendOutlined,
} from "@ant-design/icons"
import { Button, type ButtonProps } from "components/Button/Button"
import { Checkbox, type CheckboxOptionType } from "components/Checkbox/Checkbox"
import { Form, type FormRule } from "components/Form/Form"
import { Input } from "components/Input/Input"
import { Radio, type RadioGroupProps } from "components/Radio/Radio"
import { Select, type SelectProps } from "components/Select/Select"
import { Tag, type TagProps } from "components/Tag/Tag"

import { BackButton } from "components/BackButton/BackButton"
import { DatePicker } from "components/DatePicker/DatePicker"
import { Drawer } from "components/Drawer/Drawer"
import { ErrorStatus } from "components/ErrorStatus/ErrorStatus"
import { Modal } from "components/Modal/Modal"
import { useNotification } from "components/Notification/Notification"
import { PhoneInput } from "components/PhoneInput/PhoneInput"
import { SearchInput } from "components/SearchInput/SearchInput"
import { Spinner } from "components/Spinner/Spinner"
import { Table, type ColumnType } from "components/Table/Table"
import { useTableParams } from "components/Table/useTableParams"
import { Tooltip } from "components/Tooltip/Tooltip"
import { useDisclosure } from "hooks/useDisclosure"
import {
	AdminPermission,
	AdminUser,
	AdminUserInvitePayload,
	AdminUserUpdatePayload,
	BIOLOGICAL_SEXES,
	Organization,
	PREFERRED_GENDERS,
	ROLES,
	Team,
} from "interface/user"
import { memoize } from "lodash-es"
import { AlternateContactsFormItem } from "modules/patient/AlternateContactsFormItem"
import { AlternateContactsList } from "modules/patient/AlternateContactsList"
import {
	normalizePhoneNumber,
	ruleUniquePhoneNumber,
	ruleValidContactInfo,
	useCheckPhoneNumberAsync,
} from "modules/patient/contact-info"
import {
	ComponentProps,
	memo,
	ReactNode,
	useCallback,
	useMemo,
	useRef,
} from "react"
import { useConfigAdmin } from "services/globalConfig"
import {
	useAdminUser,
	useAdminUserDelete,
	useAdminUserInvite,
	useAdminUserInviteResend,
	useAdminUsers,
	useAdminUserUpdate,
} from "services/users"
import { cn } from "styles/cn"
import { sortAlpha, sortBy } from "utils"
import { asDate, Dayjs, formatDate, today } from "utils/date"
import { prettyprintPhoneNumber } from "utils/patient"
import { SelectDevice } from "./SelectDevice"
import { SelectOrganization } from "./SelectOrganization"
import { SelectTeams } from "./SelectTeams"

export const AdminUsers = () => {
	const { set, page, sort, query, role, internalOnly } = useTableParams({
		sort: ["createdAt-descending"],
		role: [],
		internalOnly: "all",
	})
	const { data: { result: users = [], total } = {}, isFetching } =
		useAdminUsers({
			sort,
			page,
			query,
			role,
			internalOnly:
				internalOnly === "all" ? undefined : internalOnly === "true",
		})
	const handleTableChange = useCallback<
		NonNullable<TableUsersType["onChange"]>
	>(
		(pagination, _, sorter) => {
			const { field, order } = Array.isArray(sorter) ? sorter[0] : sorter
			set(
				["page", String(pagination.current)],
				[
					"sort",
					field && order ? [`${field}-${order}ing`] : ["createdAt-descending"],
				],
			)
		},
		[set],
	)

	return (
		<div className="flex h-full min-h-0 flex-col gap-6">
			<div className="flex justify-between">
				<h2 className="font-nunito text-3xl font-semibold">Users</h2>
				<InviteUser />
			</div>

			<div className="flex flex-wrap gap-3">
				<SearchInput
					suffix={
						<Tooltip.Dark title="Search users by ID, Clerk ID, name, email, phone number, or device ID">
							<InfoCircleOutlined aria-hidden />
						</Tooltip.Dark>
					}
					size="large"
					value={query}
					className="max-w-sm"
					placeholder="Search users"
					onSearch={(value) => set(["query", value])}
				/>
				<SelectUserType
					size="large"
					value={role}
					allowClear
					onChange={(value) => set(["role", value])}
				/>
				<ToggleInternalOnly
					onChange={({ target: { value } }) => set(["internalOnly", value])}
				/>
			</div>

			<Table<AdminUser>
				dataSource={users}
				columns={COLUMNS}
				rowKey="id"
				loading={isFetching}
				expandable={{
					expandedRowRender: (user) => <ExpandedRowRender user={user} />,
				}}
				pagination={{
					total,
					current: Number(page),
					pageSize: 20,
				}}
				onChange={handleTableChange}
				onRow={({ fullName }) => ({
					"aria-label": `User ${fullName}`,
				})}
			/>
		</div>
	)
}

const ToggleInternalOnly = memo(function ToggleInternalOnly(
	props: RadioGroupProps,
) {
	return (
		<div
			role="group"
			aria-label="Internal only"
			className="flex items-center gap-1"
		>
			<div className="text-nowrap">Internal only</div>
			<Radio.Group
				{...props}
				size="large"
				optionType="button"
				defaultValue="all"
				options={[
					{ label: "All", value: "all" },
					{ label: "Yes", value: "true" },
					{ label: "No", value: "false" },
				]}
			/>
		</div>
	)
})

const userTypeOptions = ROLES.map((role) => ({
	value: role,
	label: role,
})).toSorted(sortBy("label"))

const SelectUserType = memo(function SelectUserType(
	props: SelectProps<string[]>,
) {
	return (
		<Select<string[]>
			{...props}
			id="select-user-types"
			mode="multiple"
			virtual={false}
			aria-label="User types"
			placeholder="Filter by one or more user types"
			className="min-w-xs"
			showSearch={{ optionFilterProp: "label" }}
			options={userTypeOptions}
		/>
	)
})

function UpdateUser({ user }: { user: AdminUser }) {
	const disclose = useDisclosure()
	const title = `Edit user ${user.fullName}`

	return (
		<>
			<Tooltip.Dark title={title}>
				<Button
					type="text"
					size="large"
					icon={<EditOutlined aria-hidden />}
					aria-label={title}
					onClick={disclose.open}
				/>
			</Tooltip.Dark>

			{disclose.isOpen && (
				<UpdateUserForm title={title} user={user} onClose={disclose.close} />
			)}
		</>
	)
}

type UpdateUserFormValues = Omit<
	AdminUserUpdatePayload,
	| "phoneNumber"
	| "dateOfBirth"
	| "careOrganizations"
	| "providerCareOrganizations"
> & {
	careOrganizations?: Organization[]
	phoneNumber?: string
	supportsTextMessages?: boolean
	dateOfBirth?: Dayjs
}

function UpdateUserForm(props: {
	title: string
	user: AdminUser
	onClose: VoidFunction
}) {
	const { notify } = useNotification()
	const [form] = Form.useForm<UpdateUserFormValues>()
	const email = Form.useWatch("email", form) ?? ""
	const { ruleCheckPhoneNumberAsync } = useCheckPhoneNumberAsync(props.user.id)
	const {
		data: user = props.user,
		isFetching,
		isFetched,
	} = useAdminUser(props.user.id)
	const update = useAdminUserUpdate(user.id)
	const { current: formName } = useRef(`update-user-${user.id}`)
	const initialValues = useMemo<UpdateUserFormValues | undefined>(() => {
		if (!user) {
			return
		}

		const [patientCareOrganization] = (user.careOrganizations ?? []).map(
			({ id }) => id,
		)

		return {
			...user,
			device: user.device?.id,
			phoneNumber: user.phoneNumber ? user.phoneNumber.value : undefined,
			supportsTextMessages: user.phoneNumber
				? user.phoneNumber.supportsTextMessages
				: undefined,
			dateOfBirth: user.dateOfBirth ? asDate(user.dateOfBirth) : undefined,
			patientCareOrganization:
				typeof patientCareOrganization === "undefined"
					? null
					: patientCareOrganization,
		}
	}, [user])
	const handleFinish = useCallback(
		({
			phoneNumber,
			supportsTextMessages,
			dateOfBirth,
			careOrganizations,
			patientCareOrganization,
			...values
		}: UpdateUserFormValues) => {
			update.mutate(
				{
					...values,
					dateOfBirth: dateOfBirth?.toISOString(),
					phoneNumber: phoneNumber
						? {
								value: phoneNumber,
								supportsTextMessages: supportsTextMessages === true,
							}
						: undefined,
					patientCareOrganization:
						patientCareOrganization !== undefined
							? patientCareOrganization
							: null,
					providerCareOrganizations: careOrganizations?.map((org) => ({
						careOrganization: org.id,
						canEnrollPatients: org.canEnrollPatients,
						canManageNurseTimes: org.canManageNurseTimes,
						communicationPermission: org.communicationPermission,
					})),
					adminPermissions: values.adminPermissions?.filter(
						({ resourcePath }) => resourcePath.length > 0,
					),
				},
				{
					onSuccess: () => {
						notify.success({
							message: `User ${values.fullName} was updated successfully`,
						})
						props.onClose()
					},
				},
			)
		},
		[notify, props, update],
	)

	return (
		<Drawer
			open
			aria-label={props.title}
			title={<BackButton onClick={props.onClose}>Users</BackButton>}
			onClose={props.onClose}
		>
			<h2 className="font-headings text-3xl font-semibold">{props.title}</h2>

			{isFetching && <Spinner />}

			{isFetched && (
				<Form<UpdateUserFormValues>
					form={form}
					layout="vertical"
					autoComplete="off"
					name={formName}
					aria-label={props.title}
					className="overflow-y-auto rounded-2xl bg-mint-0 p-2 xs:p-4"
					initialValues={initialValues}
					onFinish={handleFinish}
				>
					<Tag
						color={getUserTypeColor(user.userType)}
						className="mb-6 text-base"
					>
						{user.userType}
					</Tag>
					<Form.Item<UpdateUserFormValues>
						name="email"
						label={<InternalTag email={email} />}
						rules={[ruleValidContactInfo(), { type: "email" }]}
						dependencies={["phoneNumber", "supportsTextMessages"]}
					>
						<Input />
					</Form.Item>
					<Form.Item label="Phone number">
						<div className="flex flex-col gap-1">
							<Form.Item<UpdateUserFormValues>
								noStyle
								name="phoneNumber"
								dependencies={["email"]}
								normalize={normalizePhoneNumber}
								validateFirst
								validateDebounce={300}
								validateTrigger="onBlur"
								rules={[
									ruleValidContactInfo(),
									ruleUniquePhoneNumber(),
									ruleCheckPhoneNumberAsync,
								]}
							>
								<PhoneInput aria-label="User's phone number" />
							</Form.Item>
							<Form.Item<UpdateUserFormValues>
								noStyle
								name="supportsTextMessages"
								valuePropName="checked"
								dependencies={["phoneNumber", "email"]}
							>
								<Checkbox aria-label="User's phone supports text messages">
									Supports text messages
								</Checkbox>
							</Form.Item>
						</div>
					</Form.Item>
					<Form.Item<UpdateUserFormValues>
						name="displayName"
						label="Display name"
						rules={[
							...baseRules,
							{ whitespace: true },
							{
								pattern: /^[A-Za-z\s.'-]+$/,
								message:
									"Please enter only letters, spaces, hyphens, apostrophes, and dots",
							},
							{
								max: 100,
								message: "Display name cannot exceed 100 characters",
							},
						]}
					>
						<Input />
					</Form.Item>
					<Form.Item<UpdateUserFormValues>
						name="fullName"
						label="Full name"
						rules={[
							...baseRules,
							{ whitespace: true },
							{
								pattern: /^[A-Za-z\s.'-]+$/,
								message:
									"Please enter only letters, spaces, hyphens, apostrophes, and dots",
							},
							{
								max: 100,
								message: "Full name cannot exceed 100 characters",
							},
						]}
					>
						<Input />
					</Form.Item>
					<Form.Item<UpdateUserFormValues>
						name="excludedFromAnalytics"
						label="Exclude from analytics?"
						valuePropName="checked"
						layout="horizontal"
						colon={false}
					>
						<Checkbox aria-label="Exclude from analytics?" />
					</Form.Item>
					<Form.Item<UpdateUserFormValues>
						name="inAnonymizedView"
						label="In anonymized view?"
						valuePropName="checked"
						layout="horizontal"
						colon={false}
					>
						<Checkbox aria-label="In anonymized view?" />
					</Form.Item>
					{user.userType === "Field-support-agent" && (
						<TeamPermissionsFormItem />
					)}
					{user.userType === "Patient" && (
						<>
							<Form.Item<UpdateUserFormValues>
								name="address"
								label="Address"
								rules={[
									{ whitespace: true },
									{
										pattern: /^[a-zA-Z0-9\s,.'-]*$/,
										message: "Address cannot contain invalid characters",
									},
									{
										max: 300,
										message: "Address cannot exceed 300 characters",
									},
								]}
							>
								<Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} />
							</Form.Item>
							<Form.Item<UpdateUserFormValues>
								name="dateOfBirth"
								label="Date of birth"
							>
								<DatePicker
									className="w-full"
									format={{ format: "MM/DD/YYYY", type: "mask" }}
									placeholder="MM/DD/YYYY"
									maxDate={today().subtract(1, "day")}
								/>
							</Form.Item>
							<Form.Item<UpdateUserFormValues>
								name="biologicalSex"
								label="Biological sex"
							>
								<Radio.Group
									options={BIOLOGICAL_SEXES.map((sex) => ({
										value: sex,
										label: sex,
									}))}
								/>
							</Form.Item>
							<Form.Item<UpdateUserFormValues>
								name="preferredGender"
								label="Preferred gender"
							>
								<Radio.Group
									options={PREFERRED_GENDERS.map((gender) => ({
										value: gender,
										label: gender,
									}))}
								/>
							</Form.Item>
							<Form.Item<UpdateUserFormValues>
								name="patientCareOrganization"
								label="Care organization"
							>
								<SelectOrganization
									allowClear
									placeholder="Select a care organization"
								/>
							</Form.Item>
							<Form.Item<UpdateUserFormValues> name="device" label="Device">
								<SelectDevice />
							</Form.Item>
							<AlternateContactsFormItem
								formName={formName}
								permission="edit"
								patientPhoneNumberPath="phoneNumber"
								patientId={user.id}
							/>
						</>
					)}
					{["Provider", "Provider-anon"].includes(user.userType) && (
						<OrganizationPermissionsFormItem />
					)}
					{user.userType === "Administrator" && <AdminPermissionsFormItem />}
					<Form.Item className="pt-2">
						<div className="flex flex-col gap-2">
							<Button
								type="primary"
								htmlType="button"
								loading={update.isPending}
								className="w-full"
								onClick={form.submit}
							>
								Save
							</Button>
							<ErrorStatus show={update.isError}>
								{update.error?.message ??
									"An unexpected error occurred. Please, try again."}
							</ErrorStatus>
						</div>
					</Form.Item>
				</Form>
			)}
		</Drawer>
	)
}

function InviteUser(props: ButtonProps) {
	type InviteUserFormValues = Pick<
		AdminUserInvitePayload,
		"email" | "userType" | "adminPermissions"
	> & {
		phoneNumber: string
		supportsTextMessages: boolean
	}
	const { notify } = useNotification()
	const disclose = useDisclosure()
	const [form] = Form.useForm<InviteUserFormValues>()
	const { ruleCheckPhoneNumberAsync } = useCheckPhoneNumberAsync()
	const invite = useAdminUserInvite()
	const userType = Form.useWatch("userType", form)
	const email = Form.useWatch("email", form) ?? ""
	const label = "Invite user"
	const handleCancel = useCallback(() => {
		disclose.close()
		form.resetFields()
	}, [disclose, form])
	const handleFinish = useCallback(
		({
			phoneNumber,
			supportsTextMessages,
			adminPermissions,
			...values
		}: InviteUserFormValues) => {
			invite.mutate(
				{
					...values,
					adminPermissions: adminPermissions?.filter(
						({ resourcePath }) => resourcePath.length > 0,
					),
					phoneNumber: phoneNumber
						? { value: phoneNumber, supportsTextMessages }
						: undefined,
				},
				{
					onSuccess: () => {
						notify.success({
							message: `Invite sent to ${values.email} successfully`,
						})
						disclose.close()
						form.resetFields()
					},
				},
			)
		},
		[disclose, form, invite, notify],
	)

	return (
		<>
			<Button
				{...props}
				type="primary"
				size="large"
				icon={<SendOutlined aria-hidden />}
				onClick={disclose.open}
			>
				{label}
			</Button>

			{disclose.isOpen && (
				<Drawer
					open
					aria-label={label}
					title={<BackButton onClick={handleCancel}>Users</BackButton>}
					onClose={handleCancel}
				>
					<h2 className="font-headings text-3xl font-semibold">{label}</h2>
					<Form<InviteUserFormValues>
						form={form}
						autoComplete="off"
						name="admin-user-invite"
						layout="vertical"
						aria-label="User invite"
						initialValues={{ userType: "Provider" }}
						className="overflow-y-auto rounded-2xl bg-mint-0 p-2 xs:p-4"
						onFinish={handleFinish}
					>
						<Form.Item
							name="userType"
							label="User type"
							rules={[{ required: true, message: "Please pick a user type" }]}
						>
							<Radio.Group options={userTypeOptions} />
						</Form.Item>
						<Form.Item
							name="email"
							label={<InternalTag email={email} />}
							rules={[ruleValidContactInfo(), { type: "email" }]}
							dependencies={["phoneNumber", "supportsTextMessages"]}
						>
							<Input />
						</Form.Item>
						<Form.Item label="Phone number">
							<div className="flex flex-col gap-1">
								<Form.Item
									noStyle
									name="phoneNumber"
									dependencies={["email"]}
									normalize={normalizePhoneNumber}
									validateFirst
									validateDebounce={300}
									validateTrigger="onBlur"
									rules={[
										ruleValidContactInfo(),
										ruleUniquePhoneNumber(),
										ruleCheckPhoneNumberAsync,
									]}
								>
									<PhoneInput aria-label="User's phone number" />
								</Form.Item>
								<Form.Item
									noStyle
									name="supportsTextMessages"
									valuePropName="checked"
									dependencies={["phoneNumber", "email"]}
								>
									<Checkbox aria-label="User's phone supports text messages">
										Supports text messages
									</Checkbox>
								</Form.Item>
							</div>
						</Form.Item>
						{userType === "Administrator" && <AdminPermissionsFormItem />}
						<Form.Item className="flex flex-col gap-2 pt-2">
							<div className="flex gap-2">
								<Button
									htmlType="button"
									className="w-full"
									onClick={handleCancel}
								>
									Cancel
								</Button>
								<Button
									type="primary"
									htmlType="button"
									loading={invite.isPending}
									className="w-full"
									onClick={form.submit}
								>
									Confirm
								</Button>
							</div>
							<ErrorStatus show={invite.isError}>
								An unexpected error occurred. Please, try again.
							</ErrorStatus>
						</Form.Item>
					</Form>
				</Drawer>
			)}
		</>
	)
}

function OrganizationPermissionsFormItem() {
	const form = Form.useFormInstance<UpdateUserFormValues>()
	const careOrganizations =
		Form.useWatch<UpdateUserFormValues["careOrganizations"]>(
			"careOrganizations",
			{ form, preserve: true },
		) ?? []

	return (
		<Form.List name="careOrganizations">
			{(fields, { add, remove }) => {
				return (
					<div className="mb-6 flex flex-col gap-2">
						<div className="flex w-full items-center justify-between gap-2">
							<h3 className="text-lg font-semibold">
								Organization permissions
							</h3>
							<Button
								type="dashed"
								className="w-fit"
								icon={<PlusOutlined aria-hidden />}
								onClick={() => add({ communicationPermission: null }, 0)}
							>
								Add
							</Button>
						</div>
						<ul
							aria-label={`${fields.length} organization(s) available`}
							className={cn(
								"divide-y divide-dashed divide-grey-20 rounded-lg border border-grey-20 p-2",
								fields.length === 0 && "hidden",
							)}
						>
							{fields.map(({ key, name, ...field }, index) => {
								const organization = careOrganizations[index]

								return (
									<li
										key={key}
										className="flex flex-col not-first:pt-2 not-last:pb-3"
										aria-label={organization?.name ?? `#${index} organization`}
									>
										<Form.Item
											name={[name, "id"]}
											label="Care organization"
											layout="horizontal"
											rules={baseRules}
										>
											<SelectOrganization
												showSearch
												placeholder="Select a care organization"
												hiddenOrganizations={careOrganizations.map(
													({ id }) => id,
												)}
											/>
										</Form.Item>
										<Form.Item
											{...field}
											name={[name, "communicationPermission"]}
											label="Communication permission"
											layout="horizontal"
										>
											<Radio.Group
												aria-label="Communication permission"
												options={[
													{ label: "none", value: undefined },
													{ label: "send", value: "send" },
													{ label: "view", value: "view" },
												]}
											/>
										</Form.Item>
										<div className="flex gap-2">
											<Form.Item
												{...field}
												name={[name, "canEnrollPatients"]}
												label="Can enroll patients?"
												valuePropName="checked"
												layout="horizontal"
												colon={false}
											>
												<Checkbox aria-label="Can enroll patients?" />
											</Form.Item>
											<Form.Item
												{...field}
												name={[name, "canManageNurseTimes"]}
												label="Can manage nurse times?"
												valuePropName="checked"
												layout="horizontal"
												colon={false}
											>
												<Checkbox aria-label="Can manage nurse times?" />
											</Form.Item>
										</div>
										<div className="flex justify-end">
											<Button
												danger
												icon={<DeleteOutlined aria-hidden />}
												onClick={() => remove(index)}
											>
												Remove
											</Button>
										</div>
									</li>
								)
							})}
						</ul>
					</div>
				)
			}}
		</Form.List>
	)
}

function TeamPermissionsFormItem() {
	const form = Form.useFormInstance()
	const teams =
		Form.useWatch<AdminUser["fieldSupportTeams"] | null | undefined>(
			"fieldSupportTeams",
			{ form, preserve: true },
		) ?? []

	return (
		<Form.List name="fieldSupportTeams">
			{(fields, { add, remove }) => {
				return (
					<div className="mb-6 flex flex-col gap-2">
						<div className="flex w-full items-center justify-between gap-2">
							<h3 className="text-lg font-semibold">Team permissions</h3>
							<Button
								type="dashed"
								className="w-fit"
								icon={<PlusOutlined aria-hidden />}
								onClick={() =>
									add({ scheduling: "none", manageContacts: "none" }, 0)
								}
							>
								Add
							</Button>
						</div>
						<ul
							aria-label={`${fields.length} team(s) available`}
							className={cn(
								"divide-y divide-dashed divide-grey-20 rounded-lg border border-grey-20 p-2",
								fields.length === 0 && "hidden",
							)}
						>
							{fields.map(({ key, name, ...field }, index) => {
								const team = teams[index]

								return (
									<li
										key={key}
										className="flex flex-col not-first:pt-2 not-last:pb-3"
										aria-label={team?.name ?? `#${index} team`}
									>
										<Form.Item
											name={[name, "id"]}
											label="Team"
											layout="horizontal"
											rules={baseRules}
										>
											<SelectTeams hiddenTeams={teams.map(({ id }) => id)} />
										</Form.Item>
										<Form.Item
											{...field}
											name={[name, "canVisit"]}
											label="Can conduct visits?"
											valuePropName="checked"
											layout="horizontal"
											colon={false}
										>
											<Checkbox aria-label="Can conduct visits?" />
										</Form.Item>
										<Form.Item
											{...field}
											name={[name, "scheduling"]}
											label="Scheduling"
											layout="horizontal"
										>
											<Radio.Group
												aria-label="Scheduling"
												options={permissionsOptions}
											/>
										</Form.Item>
										<Form.Item
											{...field}
											name={[name, "manageContacts"]}
											label="Manage contacts"
											layout="horizontal"
										>
											<Radio.Group
												aria-label="Manage contacts"
												options={permissionsOptions}
											/>
										</Form.Item>
										<div className="flex justify-end">
											<Button
												danger
												icon={<DeleteOutlined aria-hidden />}
												onClick={() => remove(index)}
											>
												Remove
											</Button>
										</div>
									</li>
								)
							})}
						</ul>
					</div>
				)
			}}
		</Form.List>
	)
}

function AdminPermissionsFormItem() {
	const form = Form.useFormInstance()
	const adminPermissions =
		Form.useWatch<AdminUser["adminPermissions"]>("adminPermissions", {
			preserve: true,
		}) ?? []
	const { resources } = useConfigAdmin()
	const availablePaths = resources
		.map(({ resourcePath }) => resourcePath.join("/"))
		.filter(
			(path) =>
				!adminPermissions.some(
					(permission) => path === permission.resourcePath.join("/"),
				),
		)
	const isValid = adminPermissions.every(
		(permission) => permission.actions.length > 0,
	)
	const pathsOptions = resources
		.map(({ resourcePath }) => resourcePath.join("/"))
		.toSorted(sortAlpha)
		.map((value) => ({
			value,
			label: value,
			disabled: !availablePaths.some((path) => path === value),
		}))

	if (resources.length === 0) {
		return
	}

	return (
		<Form.List name="adminPermissions">
			{(fields, { add, remove }) => {
				return (
					<div className="mb-6 flex flex-col gap-2">
						<div className="flex w-full items-center justify-between gap-2">
							<h3 className="text-lg font-semibold">
								Administrator permissions
							</h3>
							<Button
								type="dashed"
								className="w-fit"
								disabled={!isValid || availablePaths.length === 0}
								icon={<PlusOutlined aria-hidden />}
								onClick={() =>
									add(
										{
											resourcePath: availablePaths[0].split("/"),
											actions: [],
										},
										0,
									)
								}
							>
								Add
							</Button>
						</div>

						{adminPermissions.length > 0 && (
							<ul
								aria-label={`${fields.length} permission(s) available`}
								className={cn(
									"divide-y divide-dashed divide-grey-20 rounded-lg border border-grey-20 p-2",
									fields.length === 0 && "hidden",
								)}
							>
								{fields.map(({ key, name, ...restField }, index) => {
									const permission = adminPermissions[name]
									const permissionPath =
										permission?.resourcePath?.join("/") ?? ""
									const { supportsInternalOnly, actions = [] } =
										resources.find(
											({ resourcePath }) =>
												resourcePath.join("/") === permissionPath,
										) ?? {}
									const actionsOptions = actions
										.toSorted(sortAlpha)
										.map((action) => ({
											value: action,
											label: action,
										}))

									return (
										<li
											key={key}
											className="flex flex-col not-first:pt-2 not-last:pb-3"
											aria-label={`#${index + 1} permission`}
										>
											<div className="flex gap-2">
												<Form.Item
													{...restField}
													name={[name, "resourcePath"]}
													label="Resource path"
													className="w-3/5"
													getValueProps={(value) => ({
														value: value.join("/"),
													})}
													normalize={(value) => value.split("/")}
													rules={baseRules}
												>
													<Select
														showSearch
														virtual={false}
														placeholder="Select a resource path"
														options={pathsOptions}
														onChange={() => {
															form.setFieldValue(
																["adminPermissions", name, "actions"],
																[],
															)
														}}
													/>
												</Form.Item>
												{supportsInternalOnly && (
													<Form.Item
														{...restField}
														name={[name, "internalOnly"]}
														label="Restrict to internal-only"
														valuePropName="checked"
														className="w-2/5"
													>
														<Checkbox aria-label="Restrict to internal-only" />
													</Form.Item>
												)}
											</div>
											<Form.Item
												{...restField}
												name={[name, "actions"]}
												label="Actions"
												rules={baseRules}
											>
												<Select<string[]>
													mode="multiple"
													virtual={false}
													placeholder="Select one or more actions"
													options={actionsOptions}
												/>
											</Form.Item>
											<div className="flex justify-end">
												<Button
													danger
													icon={<DeleteOutlined aria-hidden />}
													onClick={() => remove(index)}
												>
													Remove
												</Button>
											</div>
										</li>
									)
								})}
							</ul>
						)}
					</div>
				)
			}}
		</Form.List>
	)
}

function ResendInvite({ user }: { user: AdminUser }) {
	const { notify } = useNotification()
	const disclose = useDisclosure()
	const { mutate, isPending, isError } = useAdminUserInviteResend()
	const title = `Resend invite to ${user.fullName}`
	const handleOk = useCallback(() => {
		mutate(
			{ email: user.email },
			{
				onSuccess: () => {
					notify.success({
						message: `Invite sent to ${user.email} successfully`,
					})
					disclose.close()
				},
			},
		)
	}, [disclose, mutate, notify, user.email])

	return (
		<>
			<Tooltip.Dark title={title}>
				<Button
					type="text"
					size="large"
					icon={<SendOutlined aria-hidden />}
					aria-label={title}
					onClick={disclose.open}
				/>
			</Tooltip.Dark>

			{disclose.isOpen && (
				<Modal
					open
					confirmLoading={isPending}
					title={title}
					okText="Confirm"
					cancelText="Cancel"
					onOk={handleOk}
					onCancel={disclose.close}
					classNames={{ body: "flex flex-col gap-2" }}
				>
					<p>
						Are you sure you want to send the invite email to{" "}
						<b>{user.email}</b> again?
					</p>
					<ErrorStatus show={isError}>
						An unexpected error occurred. Please, try again.
					</ErrorStatus>
				</Modal>
			)}
		</>
	)
}

function DeleteUser({ user }: { user: AdminUser }) {
	const { notify } = useNotification()
	const disclose = useDisclosure()
	const title = `Delete user ${user.fullName}`
	const { mutate, isPending, isError, error } = useAdminUserDelete()
	const handleOk = useCallback(() => {
		mutate(user.id, {
			onSuccess: () => {
				notify.success({
					message: `User ${user.fullName} deleted successfully`,
				})
				disclose.close()
			},
		})
	}, [disclose, mutate, notify, user.fullName, user.id])

	return (
		<>
			<Tooltip.Dark title={title}>
				<Button
					danger
					type="text"
					size="large"
					icon={<DeleteOutlined aria-hidden />}
					aria-label={title}
					onClick={disclose.open}
				/>
			</Tooltip.Dark>

			{disclose.isOpen && (
				<Modal
					open
					confirmLoading={isPending}
					title={title}
					okText="Confirm"
					cancelText="Cancel"
					onOk={handleOk}
					onCancel={disclose.close}
					classNames={{ body: "flex flex-col gap-2" }}
				>
					Deletion can proceed if the user is not linked to any organization,
					device, data point, reviews, study responses, or study templates. Are
					you sure to delete?
					<ErrorStatus show={isError}>
						An unexpected error occurred: {error?.message}.
					</ErrorStatus>
				</Modal>
			)}
		</>
	)
}

const ExpandedRowRender = (props: { user: AdminUser }) => {
	const { data: user = props.user } = useAdminUser(props.user.id)
	const patientOnlyDetails =
		user.userType === "Patient"
			? [
					["Address", user.address],
					["Preferred gender", user.preferredGender],
					[
						"Date of birth",
						user.dateOfBirth ? formatDate(user.dateOfBirth) : undefined,
					],
					["Biological sex", user.biologicalSex],
					[
						"Device",
						user.device
							? `${user.device?.modelNumber}-${user.device?.serialNumber} (${user.device?.id})`
							: undefined,
					],
					[
						"Organization",
						user.careOrganizations?.map(({ name }) => name).join(", "),
					],
					[
						"Alternate contacts",
						<AlternateContactsList
							alternateContacts={user.alternateContacts}
							permission="view"
						/>,
					],
				]
			: []
	const agentOnlyDetails =
		user.userType === "Field-support-agent"
			? [
					[
						"Field support teams",
						<AdminTeamList teams={user.fieldSupportTeams} />,
					],
				]
			: []
	const providerOnlyDetails = ["Provider", "Provider-anon"].includes(
		user.userType,
	)
		? [
				[
					"Organizations",
					<AdminOrganizationsList organizations={user.careOrganizations} />,
				],
			]
		: []

	const administratorOnlyDetails =
		user.userType === "Administrator"
			? [
					[
						"Permissions",
						<AdminPermissionsList permissions={user.adminPermissions} />,
					],
				]
			: []
	const details = [
		["User ID", user.id],
		["Clerk ID", user.userIdentifier],
		["User type", user.userType],
		["Display name", user.displayName],
		["Full name", user.fullName],
		["Internal", user.isInternal ? "Yes" : "No"],
		["Email", user.email],
		["Phone number", prettyprintPhoneNumber(user.phoneNumber)],
		["Created at", formatDateWithTz(user.createdAt)],
		["Updated at", formatDateWithTz(user.updatedAt)],
		["Excluded from analytics", user.excludedFromAnalytics ? "Yes" : "No"],
		["In anonymized view", user.inAnonymizedView ? "Yes" : "No"],
		...providerOnlyDetails,
		...agentOnlyDetails,
		...patientOnlyDetails,
		...administratorOnlyDetails,
	]

	if (!user) {
		return null
	}

	return (
		<dl
			className="columns-xs space-y-2 text-navy-100"
			aria-label={`User details for ${user.fullName}`}
		>
			{details.map((detail) => {
				const [title, description] = detail as [ReactNode, ReactNode]
				return (
					<dt key={title as string} className="flex items-start gap-2">
						<span className="text-nowrap text-navy-60">{title}:</span>
						<span className="wrap-anywhere">{description}</span>
					</dt>
				)
			})}
		</dl>
	)
}

function AdminOrganizationsList({
	organizations = [],
}: {
	organizations?: Organization[]
}) {
	const tBool = (value: boolean) => (value ? "Yes" : "No")

	return (
		<ul
			className={cn("flex flex-col gap-1 divide-y")}
			aria-label={`${organizations.length} organization(s)`}
		>
			{organizations.map((org) => {
				return (
					<li
						key={org.id}
						className={cn("flex flex-col border-dashed")}
						aria-label={org.name}
					>
						<p className="font-semibold">{org.name}</p>
						<p>
							Communication:
							<span className="ml-1 font-semibold">
								{org.communicationPermission}
							</span>
						</p>
						<p>
							Can enroll patients?
							<span className="ml-1 font-semibold">
								{tBool(org.canEnrollPatients)}
							</span>
						</p>
						<p>
							Can manage nurse times?
							<span className="ml-1 font-semibold">
								{tBool(org.canManageNurseTimes)}
							</span>
						</p>
					</li>
				)
			})}
		</ul>
	)
}

function AdminTeamList({ teams = [] }: { teams?: Team[] }) {
	return (
		<ul
			className={cn("flex flex-col gap-1 divide-y")}
			aria-label={`${teams.length} team(s)`}
		>
			{teams.map((team) => {
				return (
					<li
						key={team.id}
						className={cn("flex flex-col border-dashed")}
						aria-label={team.name}
					>
						<p className="font-semibold">{team.name}</p>
						<p>
							Can conduct visits?
							<span className="ml-1 font-semibold">{tBool(team.canVisit)}</span>
						</p>
						<p>
							Scheduling:
							<span className="ml-1 font-semibold">{team.manageContacts}</span>
						</p>
						<p>
							Manage nurse times:
							<span className="ml-1 font-semibold">{team.scheduling}</span>
						</p>
					</li>
				)
			})}
		</ul>
	)
}

function AdminPermissionsList({
	permissions = [],
}: {
	permissions?: AdminPermission[]
}) {
	return (
		<ul
			className={cn("flex flex-col gap-1 divide-y")}
			aria-label={`${permissions.length} permission(s)`}
		>
			{permissions
				.toSorted((a, b) =>
					sortAlpha(a.resourcePath.join("/"), b.resourcePath.join("/")),
				)
				.map((permission) => {
					const path = `${permission.resourcePath.join("/")} ${permission.internalOnly ? " (Restrict to internal-only)" : ""}`
					const actions = permission.actions.join(", ")
					const ariaLabel = `Path ${path} with action(s) ${actions}`
					return (
						<li
							key={ariaLabel}
							className={cn("flex flex-col border-dashed")}
							aria-label={ariaLabel}
						>
							<p>
								Path:<span className="ml-1 font-semibold">{path}</span>
							</p>
							<p>
								Actions:<span className="ml-1 font-semibold">{actions}</span>
							</p>
						</li>
					)
				})}
		</ul>
	)
}

function InternalTag({ email }: Pick<AdminUser, "email">) {
	const { internalEmailDomains } = useConfigAdmin()
	const isInternal = internalEmailDomains.some((domain) =>
		email.includes(domain),
	)

	return (
		<span className="flex gap-1">
			Email
			{isInternal ? (
				<Tag variant="solid" color="purple">
					Internal
				</Tag>
			) : null}
		</span>
	)
}

const getUserTypeColor = memoize(
	(status: AdminUser["userType"]): TagProps["color"] => {
		if (status === "Administrator") {
			return "gold"
		} else if (status === "Field-support-agent") {
			return "purple"
		} else if (status === "Patient") {
			return "green"
		} else if (status === "Provider") {
			return "default"
		} else if (status === "Provider-anon") {
			return "blue"
		}
		return undefined
	},
)

const formatDateWithTz = (value?: string) =>
	asDate(value).format("MMM D, YYYY h:mm a z")

const renderDate: NonNullable<ColumnType<AdminUser>["render"]> = memoize(
	(value) => (value ? formatDateWithTz(value) : null),
)

const tBool = (value: boolean) => (value ? "Yes" : "No")

const COLUMNS: TableUsersType["columns"] = [
	{
		key: "operation",
		title: "Actions",
		fixed: "left",
		render(_, record) {
			return (
				<>
					<UpdateUser user={record} />
					<DeleteUser user={record} />
					{!record.emailVerified && <ResendInvite user={record} />}
				</>
			)
		},
	},
	{
		dataIndex: "userType",
		key: "userType",
		title: "User type",
		sorter: true,
		render(value) {
			return value ? <Tag color={getUserTypeColor(value)}>{value}</Tag> : null
		},
	},
	{
		dataIndex: "displayName",
		key: "displayName",
		title: "Display name",
		sorter: true,
	},
	{
		dataIndex: "fullName",
		key: "fullName",
		title: "Full name",
		sorter: true,
	},
	{
		dataIndex: "email",
		key: "email",
		title: "Email (verified?)",
		sorter: true,
		render(value, record) {
			if (record.emailVerified) {
				return (
					<>
						{value} <CheckCircleFilled aria-hidden className="text-green-60" />
					</>
				)
			}
			return value
		},
	},
	{
		dataIndex: "isInternal",
		key: "isInternal",
		title: "Internal",
		// sorter: true,
		render(value) {
			return value ? (
				<Tag variant="solid" color="purple">
					Yes
				</Tag>
			) : null
		},
	},
	{
		dataIndex: "phoneNumber",
		key: "phoneNumber",
		title: "Phone number",
		render(value) {
			return value ? prettyprintPhoneNumber(value) : null
		},
	},
	{
		dataIndex: "createdAt",
		key: "createdAt",
		title: "Created at",
		sorter: true,
		render: renderDate,
	},
	{
		dataIndex: "updatedAt",
		key: "updatedAt",
		title: "Updated at",
		sorter: true,
		render: renderDate,
	},
]

const baseRules: FormRule[] = [{ required: true }]

const permissionsOptions: CheckboxOptionType[] = [
	{ label: "none", value: "none" },
	{ label: "view", value: "view" },
	{ label: "edit", value: "edit" },
]

type TableUsersType = ComponentProps<typeof Table<AdminUser>>
